import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import {
  DocumentTabContainer,
  StyledTabPane,
  StyledTabs,
} from 'components/organisms/document/parts/document-tabs';
import { TasksTabLabel } from 'components/organisms/document/parts/document-tabs/tab-labels';
import { PrivatePlacard } from 'components/organisms/spaces/space-tab/styles';
import {
  DocumentTabsFreeMode,
  selectDocumentTabsFreeMode,
  toggleDocumentTabsFreeMode,
} from 'data/layout';
import {
  selectActiveSpaceId,
  selectSpaceData,
} from 'data/templates/projects/selectors';
import { useToggleFreeMode } from 'hooks/use-toggle-free-mode';
import { DocumentTemplateTaskPane } from './document-task-pane';
import { useRocketlaneDocumentTasks } from './use-rocketlane-document-tasks';

const EmbeddedTabPane = styled(StyledTabPane)`
  height: calc(var(--document-tab-pane-height));
`;

export const TaskPaneTab: FC<{
  docId: number;
  readOnly?: boolean;
}> = ({ docId, readOnly }) => {
  const [activeTab, setActiveTab] = useState('tasks');

  const dispatch = useDispatch();
  const documentsTabsActive = useSelector(selectDocumentTabsFreeMode);
  const onToggle = (value: boolean) => {
    dispatch(toggleDocumentTabsFreeMode(value));
  };
  const documentTabsFreeHandler = useToggleFreeMode({
    active: documentsTabsActive,
    localStorageKey: DocumentTabsFreeMode,
    onToggle,
  });

  function onTabClickHandler(tabClicked) {
    if (!documentTabsFreeHandler.active && tabClicked === activeTab) {
      documentTabsFreeHandler.enter();
    } else {
      documentTabsFreeHandler.exit();
    }
  }

  const { documentTasks } = useRocketlaneDocumentTasks({ docId });

  const spaceId = useSelector(selectActiveSpaceId());
  const space = useSelector(selectSpaceData(spaceId!));

  const { pathname } = useLocation();
  const isDocumentTemplates = pathname.includes('/templates/documents');

  return (
    <DocumentTabContainer $hidePaneContent={documentTabsFreeHandler.active}>
      <StyledTabs
        activeKey={activeTab}
        onChange={(tab) => {
          setActiveTab(tab);
        }}
        onTabClick={onTabClickHandler}
        tabPosition="right"
        tabBarStyle={{
          width: 'var(--tabs-width)',
          zIndex: 10,
          top: isDocumentTemplates ? '130px' : '48px',
        }}
      >
        <EmbeddedTabPane
          key="tasks"
          tab={<TasksTabLabel count={documentTasks.length} />}
        >
          {space?.private && (
            <PrivatePlacard>
              <FormattedMessage id="document.private.tasks" />
            </PrivatePlacard>
          )}
          <DocumentTemplateTaskPane docId={docId} readOnly={readOnly} />
        </EmbeddedTabPane>
      </StyledTabs>
    </DocumentTabContainer>
  );
};
